<template>
  <!-- 基因條（2026-10-04；品牌規範 p.27「藝術作品即品牌基因」）：照 2024 年 /arts 的原版做——
       畫面上那張圖本身（/work 卡片的圖、作品頁頁首的封面或主影片海報；瀏覽器已經載入，不多下載）原解析度放進矮框，
       scaleY(500)：框裡只剩原圖不到一列像素，拉成一根根直線＝銳利的條碼；40 秒來回慢慢掃過原圖約 10％ 的高度，條碼一點一點變
       （原版 `animation: moving 40s infinite alternate`）。滑過（整張卡或條本身）時順著慢掃的方向多走原圖高度 0.6％（千把像素高的圖約六、七列），
       2.6 秒 ease-in-out；移開時同樣慢慢回去，40 秒慢掃一直在跑、不中斷。哲宇 10/04 19:04：「hover 的時候他們移動的幅度太大了，會變得很不自然。
       hover 的部分動一點點就好，才會有作品的基因條在慢慢改變的感覺。」（原本是 1 秒跳到原圖另一段、離中心 25％，條碼整個換掉。）
       中心列與兩端暗區的左右界是預先算好的（scripts/gene/build-gene-strips.mjs → gene-strips.json 的 sources）；
       左右裁切用 CSS 把裁剩的那段撐滿條寬，但「顯示寬 × DPR」不能超過裁剩那段的原圖像素寬——超過就少裁一點，寧可留一點暗邊也不要糊。
       三層各管一件事：.gene-zoom 裁左右＋放大、.gene-pan 管滑過、img 管慢掃。圖到了才淡入（不閃模糊的）；離開視窗暫停掃描；
       減少動態不掃、不滑。裝飾性（作品名就在旁邊），不進無障礙樹；直角、不加框（DESIGN §十八）。
       img 的 loading 寫在 :src 前面：Vue 依模板順序設屬性，先設 src 會在設 lazy 之前就開始下載（2026-10-04 首頁進化量到：首頁每條基因條都把卡片原圖提前載下來，手機一進頁 12 張圖）。
       哲宇 10/04 看了兩個預先縮小的版本（9 列精靈圖、480 寬小圖）都說糊：「我原本拉那麼長就是因為這樣更有動態感，而且才會更像是 barcode」。 -->
  <span v-if="url" ref="el" class="gene-strip" :class="{ 'is-idle': !inView, 'is-loaded': loaded }" :style="style" aria-hidden="true">
    <span class="gene-zoom" :style="zoom"><span class="gene-pan"><img class="gene-img" loading="lazy" decoding="async" :src="url" alt="" @load="loaded = true" /></span></span>
  </span>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import genes from '@/data/gene-strips.json'

// slug：哪件作品；src：條碼用哪張圖（卡片傳卡片上那張；不傳就用作品頁頁首那張）
// tight：用緊裁（gene-strips.json 的 ts：慢掃經過那段最長的亮段，連中間的大段暗區也裁掉）；首頁的 HomeGene 才開，/work 照舊用 t
// label：作品頁頁首的短標籤（2026-10-05，WorkRecord 的 .work-gene，320×20／手機 240×16）：用 tl＝［左、右、中心列］。
//        build-gene-strips.mjs 預先找好「最寬、整段有色、兩端不黑」、至少 640 原圖像素的一段（原中心列找不到就換一列）。
//        放寬裁切時螢幕倍率只算到 2：3 倍手機上 240px 要 720 像素，照算會往兩邊放寬、把剛裁掉的黑邊放回來；封頂後最多約 1.1 倍放大，看不出來。
// meta：不在 gene-strips.json 的圖自己帶算好的數字（同樣的欄位；2026-10-05 實驗室頁「最近長出來的」，名單每次建置會換，建置時算，scripts/lib/lab-recent.mjs）
const props = defineProps({ slug: { type: String, required: true }, src: String, tight: Boolean, label: Boolean, meta: Object })
const HOVER = 0.006   // 滑過時多掃的量：原圖高度的 0.6％（scripts/test-home-page.mjs §10 驗 ≤ 1％、過渡 ≥ 2 秒）
const url = computed(() => props.src || genes.items[props.slug]?.page || '')
const meta = computed(() => props.meta || genes.sources[url.value] || null)   // 沒算過的圖：不裁、中心 50％
const lab = computed(() => (props.label && meta.value?.tl) || null)
const style = computed(() => {
  const c = lab.value?.[2] ?? meta.value?.c ?? 0.5, d = c <= 0.6 ? 1 : -1
  // 慢掃從中心列往有餘裕的那邊掃 10％（原版 translateY 5%→15%）；滑過時順著同一個方向多走 0.6％（HOVER），不跳段
  return { '--gene-c': c, '--gene-scan': `${-10 * d}%`, '--gene-to': Math.min(0.99, Math.max(0.01, c + HOVER * d)) }
})
// 左右界：預設照算好的裁；條寬 × DPR 比裁剩的原圖像素寬還寬，就以中點往兩邊放寬到夠
const width = ref(0)
const crop = computed(() => {
  const m = meta.value
  let [l, r] = lab.value || (props.tight && m?.ts) || m?.t || [0, 1]
  if (m?.w && width.value) {
    const dpr = globalThis.devicePixelRatio || 1
    const need = Math.min(1, (width.value * (lab.value ? Math.min(2, dpr) : dpr)) / m.w)
    if (r - l < need) { const mid = (l + r) / 2; l = mid - need / 2; r = mid + need / 2; if (l < 0) { r -= l; l = 0 } if (r > 1) { l -= r - 1; r = 1 } l = Math.max(0, l) }
  }
  return [l, r]
})
const zoom = computed(() => { const [l, r] = crop.value; return { left: `${(-l / (r - l)) * 100}%`, width: `${100 / (r - l)}%` } })
const el = ref(null), inView = ref(false), loaded = ref(false)
let io = null, ro = null
onMounted(() => {
  if (!el.value) return
  width.value = el.value.clientWidth
  if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(([e]) => { width.value = e.contentRect.width }); ro.observe(el.value) }
  if (el.value.querySelector('img')?.complete) loaded.value = true
  if (typeof IntersectionObserver === 'undefined') { inView.value = true; return }
  io = new IntersectionObserver(entries => { for (const e of entries) inView.value = e.isIntersecting }, { rootMargin: '200px 0px' })
  io.observe(el.value)
})
onBeforeUnmount(() => { io?.disconnect(); ro?.disconnect() })
</script>

<style>
.gene-strip {
  position: relative;
  display: block;
  width: 100%;
  height: var(--gene-h, 16px);
  overflow: hidden;
  opacity: 0;
}
.gene-strip.is-loaded { opacity: 1; }
/* 裁左右＋放大：原點在框的正中間，垂直拉 500 倍（照 2024 原版 scaleY(500)） */
.gene-zoom {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform-origin: 0 0;
  transform: scaleY(500);
}
/* 滑過：把中心列（或另一段）對到原點；百分比是自己的高度＝圖的高度 */
.gene-pan {
  display: block;
  transform: translateY(calc(var(--gene-c, .5) * -100%));
}
.gene-img {
  display: block;
  width: 100%;
  max-width: none;
  height: auto;
}
@media (prefers-reduced-motion: no-preference) {
  .gene-strip { transition: opacity .4s ease; }
  .gene-pan { transition: transform 2.6s cubic-bezier(.45, 0, .55, 1); }
  .gene-strip:hover .gene-pan, .gene-host:hover .gene-pan, .gene-host:focus-within .gene-pan { transform: translateY(calc(var(--gene-to, .8) * -100%)); }
  .gene-img { animation: gene-scan 40s ease-in-out infinite alternate; }
  .gene-strip.is-idle .gene-img { animation-play-state: paused; }
}
@keyframes gene-scan {
  from { transform: none; }
  to { transform: translateY(var(--gene-scan, -10%)); }
}
</style>
